import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { sourcesSurfaceQueryKey } from './useSourcesSurface';

export type OAuthPayload = { ok: true; source: string } | { ok: false; error: string };

/** Pure handler: returns true if it handled a terum-oauth-callback message. */
export function makeOAuthMessageHandler(opts: {
  onResult?: (payload: OAuthPayload) => void;
  onSuccess?: () => void;
}) {
  return (event: MessageEvent): boolean => {
    if (event?.data?.type !== 'terum-oauth-callback') return false;
    const payload = event.data.payload as OAuthPayload | undefined;
    if (payload?.ok) opts.onSuccess?.();
    if (payload) opts.onResult?.(payload);
    return true;
  };
}

/**
 * Open an OAuth popup and refetch the Sources surface when it completes.
 * The reliable signal is the popup.closed poll (cross-origin postMessage is
 * best-effort); the message handler gives immediate feedback when it arrives.
 *
 * `getUrl` is an async provider for the consent URL (e.g. an authenticated fetch
 * to mint it). The popup is opened SYNCHRONOUSLY on the click gesture — to a blank
 * page — BEFORE awaiting getUrl, because a popup opened after an awaited network
 * round-trip is blocked by the browser (Safari always). Once getUrl resolves we
 * navigate the blank popup to the real URL; if it rejects we close it and report.
 */
export function useOAuthPopup() {
  const queryClient = useQueryClient();

  const open = useCallback(
    (getUrl: () => Promise<string>, onResult?: (payload: OAuthPayload) => void) => {
      const popup = window.open('about:blank', 'terum-oauth', 'width=520,height=640');

      // A BLOCKED popup returns null. Without this branch the poll below immediately saw
      // `!popup`, treated it as "popup closed", cleaned up and refetched — and never called
      // onResult, so the caller got no signal at all: on Sources the Authorize button looked
      // simply dead, and in the onboarding wizard the step sat on "Authorize in the popup…"
      // forever, pointing at a window that was never created, inside a gate the user cannot
      // leave. Report it as the failure it is.
      if (!popup) {
        onResult?.({
          ok: false,
          error: 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
        });
        return;
      }

      let cleanup = () => {};
      const refetch = () => queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });

      const handle = makeOAuthMessageHandler({
        onSuccess: refetch,
        onResult: (payload) => {
          cleanup();
          onResult?.(payload);
        },
      });

      const interval = window.setInterval(() => {
        if (popup.closed) {
          cleanup();
          refetch(); // fallback: popup gone (message may not have crossed origins)
        }
      }, 500);

      cleanup = () => {
        window.removeEventListener('message', handle);
        window.clearInterval(interval);
      };

      window.addEventListener('message', handle);

      getUrl().then(
        (url) => {
          // Navigating a popup handle cross-origin is allowed (reading it is not).
          if (!popup.closed) popup.location.href = url;
        },
        (err) => {
          cleanup();
          if (!popup.closed) popup.close();
          onResult?.({
            ok: false,
            error: err instanceof Error ? err.message : 'Failed to start connection',
          });
        }
      );
    },
    [queryClient]
  );

  return { open };
}
